import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';
import { TableContent } from '../../../manager/components/TableContent';
import { ManagerSkill } from './types';
import { SkillCreatedBy } from './SkillCreatedBy';
import { SkillNameWithIcon } from '../common/SkillNameWithIcon';
import { useObjectLabels } from '../../../hooks/useObjectLabels';

type SkillRow = {
  name: string;
  rateable: boolean;
  employees: number;
  median: number;
  createdBy: ManagerSkill['createdBy'] | null;
  createdDate: string;
  categoryName: string;
};

type Props = {
  data: ManagerSkill[];
};

export const ManageSkillsTable: React.FC<React.PropsWithChildren<Props>> = ({
  data,
}) => {
  const { t } = useTranslation();
  const objectLabels = useObjectLabels();

  const columns: Column<SkillRow>[] = useMemo(
    () => [
      {
        Header: <Trans t={t} i18nKey="skills.manager.table.skill" />,
        accessor: 'name',
        sortType: 'caseInsensitive',
        Cell: ({ value, row }: { value: string; row: Row<SkillRow> }) => (
          <SkillNameWithIcon rateable={row.original.rateable} name={value} />
        ),
      },
      {
        Header: <Trans t={t} i18nKey="skills.manager.table.category" />,
        accessor: 'categoryName',
        sortType: 'caseInsensitive',
      },
      {
        Header: objectLabels.employeePlural,
        accessor: 'employees',
      },
      {
        Header: <Trans t={t} i18nKey="skills.manager.table.median" />,
        accessor: 'median',
      },
      {
        Header: <Trans t={t} i18nKey="skills.manager.table.createdBy" />,
        accessor: 'createdBy',
        Cell: ({
          value,
          row,
        }: {
          value: SkillRow['createdBy'];
          row: Row<SkillRow>;
        }) => {
          return value ? (
            <SkillCreatedBy
              name={value.name}
              initials={value.initials}
              avatarUrl={value.avatarUrl ?? ''}
              date={row.original.createdDate}
            />
          ) : (
            <></>
          );
        },
      },
    ],
    [t, objectLabels.employeePlural],
  );

  const dataMemo = useMemo(() => transformData(data), [data]);

  return (
    <TableContent
      columns={columns}
      data={dataMemo}
      initialSortBy={[{ id: 'name' }]}
    />
  );
};

const transformData = (data: ReadonlyArray<ManagerSkill>): SkillRow[] => {
  return data.map((s) => ({
    name: s.name,
    rateable: s.rateable,
    employees: s.employeesCount,
    median: s.median,
    createdBy: s.createdBy ?? null,
    createdDate: s.createdDate,
    categoryName: s.category?.name ?? '',
  }));
};
